/**
 * CrossMapPanel — bidirectional cross-reference matrix.
 *
 * For each rulebook code, surfaces the apex-stack conditions, theorems /
 * lemmas, attack patterns, and seams that cite it. Pivoting the matrix
 * by row or by code lets the reader trace any structural failure back
 * to every artifact it informs. The whole panel is computed once from
 * the JSON SSOTs, so it cannot drift from the rulebook.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo, useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import type { MonographDataTableColumn } from "@csk/demo-ui";
import rulebookData from "../data/rulebook.json" with { type: "json" };
import apexStackData from "../data/apexStack.json" with { type: "json" };
import theoremsData from "../data/theorems.json" with { type: "json" };
import attacksData from "../data/attacks.json" with { type: "json" };
import seamsData from "../data/seams.json" with { type: "json" };

const ACCENT = "#a78bfa";

interface Cite {
  readonly source: "apex" | "theorem" | "attack" | "seam";
  readonly id: string;
  readonly label: string;
  readonly color: string;
}

interface CodeRow {
  readonly code: string;
  readonly family: string;
  readonly title: string;
  readonly severity: "critical" | "high" | "medium";
  readonly cites: readonly Cite[];
}

interface RulebookRule {
  readonly code: string;
  readonly family: string;
  readonly title: string;
  readonly severity: "critical" | "high" | "medium";
  readonly trigger: string;
}

const FAMILY_COLORS: Readonly<Record<string, string>> = {
  SG: "#7080a0",
  DR: "#ec4899",
  E: "#38bdf8",
  P: "#22c55e",
  IC: "#a78bfa",
  CV: "#fbbf24",
};

const SOURCE_COLORS: Readonly<Record<Cite["source"], string>> = {
  apex: "#5eead4",
  theorem: "#a78bfa",
  attack: "#ec4899",
  seam: "#ef4444",
};

const SOURCE_LABELS: Readonly<Record<Cite["source"], string>> = {
  apex: "apex",
  theorem: "panelTokens/L",
  attack: "attack",
  seam: "seam",
};

function buildRows(): readonly CodeRow[] {
  const rules = rulebookData.rules as readonly RulebookRule[];

  const citationsByCode = new Map<string, Cite[]>();

  const push = (code: string, cite: Cite): void => {
    let list = citationsByCode.get(code);
    if (!list) {
      list = [];
      citationsByCode.set(code, list);
    }
    list.push(cite);
  };

  for (const c of apexStackData.conditions) {
    for (const code of c.rulebookCodes) {
      push(code, {
        source: "apex",
        id: `#${String(c.ordinal)}`,
        label: c.label,
        color: SOURCE_COLORS.apex,
      });
    }
  }
  for (const t of theoremsData.items) {
    for (const code of t.rulebookCodes) {
      push(code, {
        source: "theorem",
        id: t.id,
        label: t.label,
        color: SOURCE_COLORS.theorem,
      });
    }
  }
  for (const a of attacksData.attacks) {
    for (const code of a.primaryRules) {
      push(code, {
        source: "attack",
        id: a.id,
        label: a.label,
        color: SOURCE_COLORS.attack,
      });
    }
  }
  for (const s of seamsData.seams) {
    for (const code of s.primaryRulebookCodes) {
      push(code, {
        source: "seam",
        id: s.symbol,
        label: s.label,
        color: SOURCE_COLORS.seam,
      });
    }
  }

  return rules.map((rule) => ({
    code: rule.code,
    family: rule.family,
    title: rule.title,
    severity: rule.severity,
    cites: citationsByCode.get(rule.code) ?? [],
  }));
}

const ROWS = buildRows();

const FAMILIES = [
  { id: "all", label: "All families", color: ACCENT },
  ...rulebookData.families.map((f) => ({
    id: f.id,
    label: `${f.id} — ${f.label}`,
    color: FAMILY_COLORS[f.id] ?? panelTokens.muted,
  })),
];

const CITED_VALUES = ["all", "cited", "uncited"] as const;
type CitedFilter = (typeof CITED_VALUES)[number];

const CITED_LABELS: Readonly<Record<CitedFilter, string>> = {
  all: "All rules",
  cited: "Cited by ≥ 1 artifact",
  uncited: "Schema-gate only",
};

const CROSS_MAP_COLUMNS: readonly MonographDataTableColumn<CodeRow>[] = [
  {
    id: "code",
    header: "Rulebook code",
    cell: ({ row }) => <RulebookCodeCell row={row.original} />,
  },
  {
    id: "cites",
    header: "Cited by",
    cell: ({ row }) => <RulebookCitationsCell cites={row.original.cites} />,
  },
];

export function CrossMapPanel(): React.JSX.Element {
  const [family, setFamily] = useState<string>("all");
  const [cited, setCited] = useState<CitedFilter>("all");
  const [query, setQuery] = useState<string>("");

  const filtered = useMemo<readonly CodeRow[]>(() => {
    const needle = query.trim().toLowerCase();
    return ROWS.filter((row) => {
      if (family !== "all" && row.family !== family) return false;
      if (cited === "cited" && row.cites.length === 0) return false;
      if (cited === "uncited" && row.cites.length > 0) return false;
      if (needle.length > 0) {
        const hay = `${row.code} ${row.title}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [family, cited, query]);

  const counts = useMemo(() => {
    const total = ROWS.length;
    const cite = ROWS.filter((r) => r.cites.length > 0).length;
    return { total, cite, uncited: total - cite };
  }, []);

  return (
    <PanelSurface.PanelShell
      title="Cross-Reference Map"
      subtitle="Every rulebook code · every artifact that cites it"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14.5px] leading-[1.7] text-[var(--ilg-text)]">
          The cross-map is the structural ledger: every rulebook code on one
          axis, every artifact that cites it on the other. The coverage is
          irredundant by construction — every accepted theorem, attack,
          apex-stack condition, and seam carries at least one rule binding, and
          most rules are cited by more than one. Filter by family or by citation
          density to walk the matrix along whichever axis the question demands.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Filter</PanelSurface.SectionHeading>
        <div className="mt-3.5 flex flex-wrap gap-3 items-center">
          <PanelSurface.TextField
            value={query}
            onChange={setQuery}
            placeholder="code or title…"
            ariaLabel="Filter by code or title"
            label="search"
            mono
            minWidth={220}
          />
          <div
            role="tablist"
            aria-label="Rulebook family"
            className="flex flex-wrap gap-1.5"
          >
            {FAMILIES.map((f) => {
              const active = f.id === family;
              return (
                <PanelSurface.ChoiceChip
                  key={f.id}
                  id={f.id}
                  label={f.label}
                  active={active}
                  onSelect={setFamily}
                  color={f.color}
                />
              );
            })}
          </div>
          <div className="flex gap-1.5">
            {CITED_VALUES.map((v) => {
              const active = v === cited;
              return (
                <PanelSurface.ChoiceChip
                  key={v}
                  id={v}
                  label={CITED_LABELS[v]}
                  active={active}
                  onSelect={setCited}
                  color={ACCENT}
                />
              );
            })}
          </div>
        </div>
        <div className="mt-3.5 flex gap-2.5 flex-wrap">
          {PanelSurface.badge(
            `${filtered.length} / ${counts.total} shown`,
            panelTokens.muted,
          )}
          {PanelSurface.badge(`${counts.cite} cited`, panelTokens.accentGreen)}
          {PanelSurface.badge(
            `${counts.uncited} schema-gate only`,
            panelTokens.muted,
          )}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card className="p-0 overflow-hidden">
        <PanelSurface.DataTable
          ariaLabel="Rulebook code citations"
          data={filtered}
          columns={CROSS_MAP_COLUMNS}
          getRowId={(row) => row.code}
          compact
          empty="No rules match the current filter."
          minWidth={680}
        />
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Legend</PanelSurface.SectionHeading>
        <div className="mt-3 flex gap-2.5 flex-wrap">
          {(Object.keys(SOURCE_COLORS) as Cite["source"][]).map((src) =>
            PanelSurface.badge(SOURCE_LABELS[src], SOURCE_COLORS[src]),
          )}
        </div>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}

function RulebookCodeCell({
  row,
}: {
  readonly row: CodeRow;
}): React.JSX.Element {
  const familyColor = FAMILY_COLORS[row.family] ?? panelTokens.muted;
  const severityColor =
    row.severity === "critical"
      ? panelTokens.accentRed
      : row.severity === "high"
        ? panelTokens.accentAmber
        : panelTokens.accentBlue;
  return (
    <div className="min-w-[150px]">
      <div
        className="font-mono font-extrabold text-[13px]"
        style={{
          color: familyColor,
          textShadow: `0 0 6px ${familyColor}33`,
        }}
      >
        {row.code}
      </div>
      <div className="mt-1 text-[11.5px] text-[var(--ilg-text)] leading-[1.4]">
        {row.title}
      </div>
      <div className="mt-1.5 flex gap-1.5 flex-wrap">
        {PanelSurface.badge(row.severity, severityColor)}
      </div>
    </div>
  );
}

function RulebookCitationsCell({
  cites,
}: {
  readonly cites: readonly Cite[];
}): React.JSX.Element {
  if (cites.length === 0) {
    return (
      <span className="text-[11.5px] text-[var(--ilg-muted)] italic font-mono">
        (schema-gate / coverage rule — fires at JSON validation only)
      </span>
    );
  }
  return (
    <div className="flex gap-1.5 flex-wrap items-start">
      {cites.map((c, idx) => (
        <span
          key={`${c.source}-${c.id}-${String(idx)}`}
          title={c.label}
          className="inline-flex items-center gap-1.5 px-[9px] py-[3px] rounded-lg font-mono text-[11px] font-semibold"
          style={{
            background: `${c.color}1c`,
            border: `1px solid ${c.color}55`,
            color: c.color,
          }}
        >
          <span className="opacity-70">{SOURCE_LABELS[c.source]}</span>
          <span className="text-[var(--ilg-bright)] font-bold">{c.id}</span>
        </span>
      ))}
    </div>
  );
}
